/**
 * RequirementsList Component
 *
 * Displays and manages guide approval requirements with status updates,
 * priority indicators, and due date tracking.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-146 - Guide Approval Workflow Enhancement (34 SP)
 */

import {
  Clock,
  AlertTriangle,
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import { useState } from 'react';
import StatusBadge from '../common/StatusBadge';
import Badge from '../common/Badge';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import Modal from '../common/Modal';
import Alert from '../common/Alert';

/**
 * Get StatusBadge variant for requirement status
 */
function getStatusVariant(status) {
  const variants = {
    pending: 'warning',
    submitted: 'info',
    reviewing: 'info',
    approved: 'success',
    rejected: 'error',
    waived: 'neutral',
  };
  return variants[status] || 'neutral';
}

/**
 * Get Badge color for priority
 */
function getPriorityBadgeColor(priority) {
  const colors = {
    low: 'gray',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
  };
  return colors[priority] || 'gray';
}

/**
 * Format requirement type for display
 */
function formatType(type) {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format status for display
 */
function formatStatus(status) {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export default function RequirementsList({
  requirements,
  onUpdateStatus,
  onDelete,
  onAdd,
  loading = false,
  readOnly = false,
}) {
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const handleStatusUpdate = async () => {
    if (!selectedRequirement || !newStatus) {return;}

    await onUpdateStatus(selectedRequirement.id, newStatus, reviewNotes);
    setShowStatusModal(false);
    setSelectedRequirement(null);
    setNewStatus('');
    setReviewNotes('');
  };

  const openStatusModal = (requirement) => {
    setSelectedRequirement(requirement);
    setNewStatus(requirement.status);
    setReviewNotes('');
    setShowStatusModal(true);
  };

  if (loading) {
    return (
      <LoadingSpinner message="Loading requirements..." size="sm" />
    );
  }

  if (!requirements || requirements.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="No requirements"
        description="No approval requirements have been created"
        action={!readOnly && onAdd ? (
          <button
            onClick={onAdd}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-brand-primary hover:bg-brand-secondary transition-colors"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Requirement
          </button>
        ) : undefined}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">
          Requirements ({requirements.length})
        </h3>
        {!readOnly && onAdd && (
          <button
            onClick={onAdd}
            className="inline-flex items-center px-2.5 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-brand-primary hover:bg-brand-secondary transition-colors"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </button>
        )}
      </div>

      {/* Requirements List */}
      <div className="space-y-2">
        {requirements.map((req) => {
          const isOverdue = req.due_date && new Date(req.due_date) < new Date();
          const isPending = ['pending', 'submitted'].includes(req.status);

          return (
            <div
              key={req.id}
              className={`border rounded-lg p-3 ${
                isOverdue && isPending
                  ? 'border-red-300 bg-red-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex justify-between items-start">
                {/* Left side - Title and Description */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {req.title}
                    </span>
                    <Badge color={getPriorityBadgeColor(req.priority)}>
                      {req.priority === 'urgent' && <AlertTriangle className="w-3 h-3 mr-1 inline" />}
                      {req.priority.toUpperCase()}
                    </Badge>
                  </div>

                  {req.description && (
                    <p className="text-xs text-gray-600 mb-2">{req.description}</p>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    <span className="flex items-center">
                      Type: <span className="ml-1 font-medium">{formatType(req.type)}</span>
                    </span>
                    {req.due_date && (
                      <span className={`flex items-center ${isOverdue && isPending ? 'text-red-600 font-medium' : ''}`}>
                        <Clock className="w-3 h-3 mr-1" />
                        Due: {new Date(req.due_date).toLocaleDateString()}
                        {isOverdue && isPending && ' (Overdue)'}
                      </span>
                    )}
                    {req.reviewed_by && (
                      <span>
                        Reviewed by: {req.reviewed_by.name}
                      </span>
                    )}
                  </div>

                  {req.review_notes && (
                    <Alert variant="info" className="mt-2">
                      <span className="font-medium">Review Notes:</span> {req.review_notes}
                    </Alert>
                  )}
                </div>

                {/* Right side - Status and Actions */}
                <div className="flex items-start gap-2 ml-3">
                  <button
                    onClick={() => openStatusModal(req)}
                    disabled={readOnly}
                    className={`${!readOnly ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                  >
                    <StatusBadge variant={getStatusVariant(req.status)}>
                      {formatStatus(req.status)}
                    </StatusBadge>
                  </button>

                  {!readOnly && onDelete && (
                    <button
                      onClick={() => onDelete(req.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Delete Requirement"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal && selectedRequirement !== null}
        onClose={() => setShowStatusModal(false)}
        title="Update Requirement Status"
        size="md"
        footer={
          <div className="flex justify-end gap-3 w-full">
            <button
              onClick={() => setShowStatusModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleStatusUpdate}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-md hover:bg-brand-secondary"
            >
              Update Status
            </button>
          </div>
        }
      >
        {selectedRequirement && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm"
              >
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="reviewing">Reviewing</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="waived">Waived</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Notes
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm"
                placeholder="Add notes about this status change..."
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
